import { Typography } from '@mui/material'
import parse from 'html-react-parser'
import { useTranslation } from 'react-i18next'
import { removeNewLinesFromEnd } from '@omnea-app/frontend/components/shared/form/inputs/rich-text-input/utils'
import { CSS_CLASSES } from './constants'
import { SpanWithQuillStyles } from './SpanWithQuillStyles'
import { UserMention } from './UserMention'

interface Props {
  commentText: string
  variant: 'MainBody' | 'Compact' | 'Sidebar'
  isAmendment?: boolean
  isTaskRejection?: boolean
  stepLabel?: string
  showMoreInfoTooltip?: boolean
  mentionedUsers?: {
    id: string
    userId: string | null
    user: {
      firstName: string
      lastName: string
      email: string
      isOutOfOffice: boolean
    } | null
  }[]
}

export function CommentStringWithMentions({
  commentText,
  variant,
  isAmendment = false,
  isTaskRejection = false,
  stepLabel,
  showMoreInfoTooltip = false,
  mentionedUsers,
}: Props) {
  const { t } = useTranslation('requests')
  const fontVariant = variant === 'MainBody' || variant === 'Compact' ? 'Body14pxRegular' : 'Body12pxRegular'

  const htmlWithStyledMentions = styleMentions(commentText)
  const trimmedValue = removeNewLinesFromEnd(htmlWithStyledMentions)

  const parsedHtml = parse(trimmedValue, {
    trim: true,
    replace: (node) => {
      // strip off any style tags that might make the comment unreadable
      if ('attribs' in node && node.attribs && node.attribs.style) {
        delete node.attribs.style
      }

      // Handle comment mentions by wrapping them with Tooltip
      if (
        'attribs' in node && // 1. Check if the parsed HTML node has attributes
        node.children && // 2. Ensure the node has children
        node.attribs && // 3. Check if attributes object exists
        node.attribs.class === CSS_CLASSES.COMMENT_MENTION && // 4. Check if it's a mention element
        node.attribs['data-user-id'] // 5. Ensure the data-user-id attribute exists
      ) {
        const mentionedUser = mentionedUsers?.find((user) => user.userId === node.attribs['data-user-id'])
        const fallbackText = node.children && node.children[0] && 'data' in node.children[0] ? node.children[0].data : ''
        return <UserMention mentionedUser={mentionedUser} showMoreInfoTooltip={showMoreInfoTooltip} fallbackText={fallbackText} />
      }
    },
  })

  return (
    <Typography variant={fontVariant}>
      {isAmendment && <strong> {t('request-comments.amendment-requested')}: </strong>}
      {isTaskRejection &&
        (stepLabel ? (
          <strong> {t('request-comments.rejection-with-step-label', { stepLabel })}: </strong>
        ) : (
          <strong> {t('request-comments.rejection')}: </strong>
        ))}
      <SpanWithQuillStyles
        sx={{
          wordBreak: 'break-word',
          lineHeight: '20px',
          '& a': {
            color: 'secondary.main',
          },
          [`& .${CSS_CLASSES.COMMENT_MENTION}`]: {
            padding: '2px 6px',
            backgroundColor: 'neutralLight.50',
            borderRadius: '4px',
            fontWeight: '500',
            color: 'neutralDark.700',
            cursor: showMoreInfoTooltip ? 'pointer' : 'default',
          },
          '& .step-name': { color: 'secondary.main' },
          img: {
            maxWidth: '100%',
            height: 'auto',
          },
        }}
      >
        {parsedHtml}
      </SpanWithQuillStyles>
    </Typography>
  )
}

export const MENTION_REGEX = /@\[(.*?)]\((.*?)\)/g

const styleMentions = (comment: string) => {
  const result = comment.replace(MENTION_REGEX, (_, name, id) => {
    return `<span class="${CSS_CLASSES.COMMENT_MENTION}" data-user-id="${id}">@${name}</span>`
  })
  return result
}
